import { useCallback, useMemo, useState, type MouseEvent } from "react";
import { Box, Button, Collapse, Group, List, Stack, Text } from "@mantine/core";
import { IconCheck, IconChevronDown, IconClock } from "@tabler/icons-react";
import dayjs from "dayjs";
import type { CaseStatusHistoryItem } from "../../../services/cases.service";

interface CaseStatusHistoryPanelProps {
  statusHistory: CaseStatusHistoryItem[];
}

export default function CaseStatusHistoryPanel({
  statusHistory,
}: CaseStatusHistoryPanelProps) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const durations = useMemo(
    () => statusDurations(statusHistory),
    [statusHistory]
  );
  const handleToggleRequest = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      const eventId = event.currentTarget.dataset.eventId;
      if (!eventId) return;
      setExpandedIds((current) => {
        const next = new Set(current);
        if (next.has(eventId)) next.delete(eventId);
        else next.add(eventId);
        return next;
      });
    },
    []
  );

  if (statusHistory.length === 0) {
    return (
      <Box py="xl" ta="center">
        <IconClock
          size={40}
          color="var(--mantine-color-gray-4)"
          style={{ marginBottom: 8 }}
        />
        <Text size="sm" c="dimmed">
          No status history available
        </Text>
      </Box>
    );
  }

  return (
    <Stack gap="md" role="list" aria-label="Case status timeline">
      {statusHistory.map((event, index) => {
        const isExpanded = expandedIds.has(event.id);
        const disclosureId = `history-details-${event.id.replace(
          /[^a-zA-Z0-9_-]/g,
          "-"
        )}`;
        const itemCount = event.requestedItems?.length ?? 0;
        const duration = durations.get(event.id);
        return (
          <Box
            key={event.id}
            role="listitem"
            p="md"
            bg={index === 0 ? "blue.0" : "gray.0"}
            style={(theme) => ({
              border: `1px solid ${theme.colors.gray[2]}`,
              borderRadius: theme.radius.md,
            })}
          >
            <Group align="flex-start" wrap="nowrap">
              <Box
                mt={2}
                bg={index === 0 ? "blue.7" : "gray.2"}
                c={index === 0 ? "white" : "gray.6"}
                style={{
                  alignItems: "center",
                  borderRadius: "50%",
                  display: "flex",
                  flex: "0 0 32px",
                  height: 32,
                  justifyContent: "center",
                }}
              >
                <IconCheck size={15} aria-hidden="true" />
              </Box>
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Group justify="space-between" align="flex-start" gap="xs">
                  <Text size="sm" fw={700}>
                    {event.title || event.toStatus?.name || "Case activity"}
                  </Text>
                  <Text
                    size="xs"
                    fw={500}
                    c={index === 0 ? "blue.8" : "dimmed"}
                  >
                    {dayjs(event.changedAt).format("MMM D, h:mm A")}
                  </Text>
                </Group>
                <Text size="sm" c="dimmed" mt={2}>
                  Processed by{" "}
                  <Text span fw={600} c="dark">
                    {event.changedBy?.fullName ?? "Actor unavailable"}
                  </Text>
                  {duration ? ` · Duration: ${duration}` : ""}
                </Text>
                {event.eventType === "additional_stain_request" &&
                itemCount > 0 ? (
                  <Box mt="xs">
                    <Button
                      variant="subtle"
                      size="compact-sm"
                      px={0}
                      rightSection={
                        <IconChevronDown
                          size={14}
                          style={{
                            transform: isExpanded
                              ? "rotate(180deg)"
                              : undefined,
                          }}
                        />
                      }
                      aria-expanded={isExpanded}
                      aria-controls={disclosureId}
                      data-event-id={event.id}
                      onClick={handleToggleRequest}
                    >
                      {isExpanded ? "Hide" : "Show"} requested stains (
                      {itemCount})
                    </Button>
                    <Collapse in={isExpanded}>
                      <Box
                        id={disclosureId}
                        mt="xs"
                        pl="xs"
                        mah={240}
                        style={{ overflowY: "auto" }}
                      >
                        <List size="sm" spacing={4}>
                          {event.requestedItems?.map((item) => (
                            <List.Item
                              key={`${item.stainCode}-${item.specimenNumber}-${item.blockNumber}`}
                            >
                              <Text span fw={600}>
                                {item.stainCode}
                              </Text>{" "}
                              — Specimen {item.specimenNumber}
                              {item.blockNumber
                                ? `, Block ${item.blockNumber}`
                                : ""}
                              {item.quantity > 1
                                ? ` (${item.quantity} slides)`
                                : ""}
                            </List.Item>
                          ))}
                        </List>
                      </Box>
                    </Collapse>
                  </Box>
                ) : null}
              </Box>
            </Group>
          </Box>
        );
      })}
    </Stack>
  );
}

function statusDurations(events: CaseStatusHistoryItem[]): Map<string, string> {
  const statuses = events.filter((event) => event.eventType === "status");
  return statuses.reduce((durations, event, index) => {
    const newerStatus = statuses[index - 1];
    if (newerStatus)
      durations.set(
        event.id,
        formatDuration(event.changedAt, newerStatus.changedAt)
      );
    return durations;
  }, new Map<string, string>());
}

function formatDuration(start: string, end: string): string {
  const minutes = dayjs(end).diff(dayjs(start), "minute");
  if (minutes < 60) return `${minutes}m`;
  if (minutes < 1440) return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
  return `${Math.floor(minutes / 1440)}d`;
}
